/* eslint-disable padding-line-between-statements */
import React, { useCallback, useContext, useMemo, useEffect, useState } from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useModal } from '@jutro/components';

import { TWIAAccountHolderEdit } from 'twia-capability-quoteandbind-residential-react'
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { TWIAResourcesService } from 'twia-capability-constants';
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
// eslint-disable-next-line import/no-relative-packages
import _TWIADateUtil from "../../../../modules/twia-portals-viewmodel-js/aspects/_TWIADateUtil";
import metadata from './PolicyDetailsPage.metadata.json5';
import messages from './PolicyDetails.messages.js';


function PolicyDetailsPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext)
    const { wizardData: submissionVM, updateWizardData, id } = props;
    const [ pcConstants, setPCConstants ] = useState([]);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid, initialValidation } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const [editPrimaryName, setEditPrimaryName] = useState(false);
    const { showAlert } = useModal();
    const { refreshData } = useDataRefresh();
    const isHomeowners = _.get(submissionVM, 'baseData.productCode.value') === 'Homeowners';
    const coastalZoneType =  _.get(submissionVM, 'lobData.homeowners.coverables.yourHome.coastalZoneType_TWIA.value'); // DE18692
    const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');
    const isTWIA = selectedUWCompany === 'twia'

    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
   const getPCConstants = useCallback(async () => {
        const constants = await TWIAResourcesService.getPCPortalConstants(authHeader)
        setPCConstants(constants);
    }, [authHeader]);
    

    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
    useMemo(() => {
        getPCConstants();
    }, [getPCConstants]);

    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
    // Replace hard-coded frontend max effective date validation with use of backend DTO validation which uses script parameters to define the 
    // max effective days in the future for each Association
    useEffect(() => {
        _TWIADateUtil.setMaxEffectiveDays(
            isTWIA
            ? pcConstants.maxEffectiveDateFromTodayForPEPortal_TWIA 
            : pcConstants.maxEffectiveDateFromTodayForPEPortal_TFPA)
    }, [isTWIA, submissionVM,
        pcConstants.maxEffectiveDateFromTodayForPEPortal_TFPA, pcConstants.maxEffectiveDateFromTodayForPEPortal_TWIA]);

    
    const onNext = useCallback(async () => {
        if (editPrimaryName) {
            return showAlert({
                title: messages.saveModalTitle,
                message: messages.saveModalText,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            }).then(() => false)
        }

        if (!isComponentValid) {
            setShowErrors(true)

            return false
        }

        try {
            _.unset(submissionVM.value, 'bindData');

            // DE18250 DE18692 DE19764
            if (isHomeowners) {
                if (
                    coastalZoneType === undefined ||
                    !_.get(submissionVM, 'lobData.homeowners.coverables.yourHome.aspects.valid') ||
                    !_.get(submissionVM, 'lobData.homeowners.coverables.yourHome.aspects.subtreeValid')
                ) {
                    _.set(submissionVM, 'lobData.homeowners.coverables.yourHome', undefined);
                }
            
                if (
                    !_.get(submissionVM, 'lobData.homeowners.coverables.construction.aspects.valid') ||
                    !_.get(submissionVM, 'lobData.homeowners.coverables.construction.aspects.subtreeValid')
                ) {
                    _.set(submissionVM, 'lobData.homeowners.coverables.construction', undefined);
                }

                if (
                    !_.get(submissionVM, 'lobData.homeowners.offerings.aspects.valid') ||
                    !_.get(submissionVM, 'lobData.homeowners.offerings.aspects.subtreeValid')
                ) { 
                    _.set(submissionVM, 'lobData.homeowners.offerings', undefined); 
                }
            }

            const newSubmissionVM = viewModelService.clone(submissionVM);
            // _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmission(
                newSubmissionVM.value,
                authHeader
            )

            return submissionVM
        } catch (error) {
            console.log(error)
            ErrorPopupUtil.showSaveErrorPopup(showAlert, isTWIA);
            
            return false;
            // return submissionVM
        }
    }, [editPrimaryName, isComponentValid, showAlert, submissionVM, isHomeowners, coastalZoneType,
        viewModelService, LoadSaveService, authHeader, isTWIA]);

    // US26261-Done changes for Commercial Property flow
    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [refreshData, submissionVM, updateWizardData]
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        accountHolderEdit: {
            fieldLabel: _.get(submissionVM, 'baseData.accountHolder.subtype.value') === 'Person' ? messages.fieldLabel : messages.fieldLabelCompany,
            submissionVM,
            updateWizardData,
            editPrimaryName,
            setEditPrimaryName
        },
        // US26261-Done changes for Commercial Property flow
        spaceAccountHolder: {
            visible: submissionVM.value.baseData.productName === "Commercial"
        },
        description: {
            visible: submissionVM.value.baseData.productName === "Commercial"
        },
        spaceDescription: {
            visible: submissionVM.value.baseData.productName === "Commercial"
        },
        companyName: {
            visible: submissionVM.value.baseData.productName === "Commercial"
        }
    };

    const resolvers = {
        resolveComponentMap: {
            TWIAAccountHolderEdit
        },
        resolveClassNameMap: wizardStyles
    }

    return (

        <WizardPage
            id={id}
            showPrevious={false}
            onNext={onNext}
            skipWhen={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                showErrors={showErrors}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onValueChange={writeValue}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

PolicyDetailsPage.propTypes = wizardProps;
export default PolicyDetailsPage;
